/**
 * Live mode (operators only, 2026-10-18): the live match sessions, newest first, and "Start a match".
 * A session's segments are uploaded on its own page (LiveSession.tsx).
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { live, type LiveSessionBrief } from "../api/live";
import { useLoad, usePoll } from "../api/hooks";
import { useAuth } from "../auth/AuthContext";
import { Shell } from "../components/Shell";
import { ErrorLine } from "../components/ui";
import { fmtDuration, plural } from "../lib/format";
import { OperatorsOnly } from "./AdminPipeline";

export default function LiveSessions() {
  const { me } = useAuth();
  const nav = useNavigate();
  const list = useLoad(() => live.sessions(), []);
  usePoll(list.reload, 15_000, !!me?.user.is_operator);
  const [title, setTitle] = useState("");
  const [choice, setChoice] = useState<"full" | "junior" | "unknown">("full");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<Error | null>(null);
  if (me && !me.user.is_operator) return <OperatorsOnly shows="live match sessions" />;
  const start = async () => {
    if (!title.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await live.create(title.trim(), choice);
      nav(`/admin/live/${r.session.id}`);
    } catch (e) {
      setErr(e as Error);
      setBusy(false);
    }
  };
  const items: LiveSessionBrief[] = list.data?.items ?? [];
  return (
    <Shell title="Live" back="/">
      <div className="p">
        <h1 className="mt16">Live matches</h1>
        <p className="sub mt6">Upload a match as it is played, one segment at a time. The first segment is calibrated and every later segment uses that calibration; each segment goes to label review once its balls are found.</p>
        <div className="card mt24 lv-new" data-testid="live-new">
          <div className="h3">Start a match</div>
          <label className="lv-field mt10">
            <span className="tiny">Match</span>
            <input className="field" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Lords v Vikings, semi-final" maxLength={200} data-testid="live-title" />
          </label>
          <label className="lv-field mt10">
            <span className="tiny">Pitch length</span>
            <select className="field" value={choice} onChange={(e) => setChoice(e.target.value as "full" | "junior" | "unknown")} data-testid="live-pitch">
              <option value="full">Full (22 yards)</option>
              <option value="junior">Junior (13.7 m)</option>
              <option value="unknown">Not sure</option>
            </select>
          </label>
          <button className="btn dark mt16" disabled={busy || !title.trim()} onClick={start} data-testid="live-start">{busy ? "Starting…" : "Start the match"}</button>
          <ErrorLine error={err} />
        </div>
        <ErrorLine error={list.error} />
        <div className="list mt24" data-testid="live-sessions">
          {items.map((s) => (
            <Link key={s.id} to={`/admin/live/${s.id}`} className="row between lv-srow" data-testid="live-session-row">
              <div>
                <div className="med">{s.title}</div>
                <div className="tiny">{plural(s.segments ?? 0, "segment")} · {s.segments_ready ?? 0} ready{s.duration_ms ? ` · ${fmtDuration(s.duration_ms)}` : ""}{s.camera_setup_id ? " · calibrated" : ""}</div>
              </div>
              <span className="tiny">{new Date(s.created_at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} ›</span>
            </Link>
          ))}
          {!list.loading && items.length === 0 && <p className="tiny">No live matches yet.</p>}
        </div>
      </div>
    </Shell>
  );
}
